import * as React from 'react';
import { FC } from 'react';
import {
    List,
    Datagrid,
    TextField,
    useTranslate,
    useEditContext,
    ListProps,
    Pagination,
    TextInput,
    TopToolbar,
    ExportButton,
    downloadCSV,
    useNotify,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import CustomEmpty from './CustomEmpty';
import CustomSelectInput from './CustomSelectInput';
import CustomUserLicenseField from './CustomUserLicenseField';

interface Props extends ListProps {
    bulkActionButtons?:
        | false
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | undefined;
    sortable?: boolean;
    showExtra?: any;
    hideFields?: any;
    customFilters?: any;
    refreshKey?: string;
    enableExport?: boolean;
}

const LICENSE_STATUS_LABELS: Record<number, string> = {
    0: 'Käytössä',
    1: 'Poistettu',
};

// dd.mm.yyyy, tyhjä jos arvo puuttuu tai ei ole kelvollinen päivämäärä
const formatDate = (value: any) => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getDate())) return '';
    const pad = '00';
    return `${(pad + date.getDate()).slice(-2)}.${(pad + (date.getMonth() + 1)).slice(-2)}.${date.getFullYear()}`;
};

// Excel tulkitsee pitkät numeromaiset tekstit (päivämäärät, ISBN) joskus luvuksi/ajaksi/
// tieteelliseksi notaatioksi. ="..." pakottaa Excelin näyttämään arvon tekstinä sellaisenaan.
const asExcelText = (value: any) => (value ? `="${value}"` : '');

const formatDateForExcel = (value: any) => asExcelText(formatDate(value));

const CustomUserLicenseList: FC<Props> = ({
    bulkActionButtons = false,
    sortable = true,
    showExtra = [],
    hideFields = [],
    customFilters = [],
    refreshKey = 'refreshAll',
    enableExport = false,
    ...props
}) => {
    const translate = useTranslate();
    const parent = useEditContext();
    const notify = useNotify();
    const filter = customFilters.includes('subscription_guid')
        ? { subscription_guid: parent.record.guid }
        : undefined;

    // Sama tuote jokaisella rivillä, koska lista on rajattu yhteen tilaukseen (subscription_guid).
    const productName = parent.record?.product?.name ?? '';
    const productIsbn =
        parent.record?.product?.public?.materialstorage?.isbn ?? '';

    const exporter = users => {
        // Yksi rivi per käyttäjän lisenssirivi, koska käyttäjällä voi olla useita (historia).
        const rows = users.flatMap(user =>
            (user.licenses && user.licenses.length > 0
                ? user.licenses
                : [{}]
            ).map(license => ({
                Sukunimi: user.lastname,
                Etunimi: user.firstname,
                Sähköposti: user.email,
                Tuote: productName,
                ISBN: asExcelText(productIsbn),
                Luotu: formatDateForExcel(license.created),
                'Voimassa alkaen': formatDateForExcel(license.valid_from),
                'Voimassa asti': formatDateForExcel(license.valid_until),
                Tila:
                    LICENSE_STATUS_LABELS[license.status] ??
                    license.status ??
                    '',
                Keskeytetty: formatDateForExcel(license.discontinued),
                Palautettu: formatDateForExcel(license.returned),
                Jatkettu: license.extended ? 'Kyllä' : '',
                Vaihdettu: formatDateForExcel(license.changed),
                Järjestysnro: license.reduce ? license.license_order_nro : '',
            }))
        );
        jsonExport(
            rows,
            {
                headers: [
                    'Sukunimi',
                    'Etunimi',
                    'Sähköposti',
                    'Tuote',
                    'ISBN',
                    'Luotu',
                    'Voimassa alkaen',
                    'Voimassa asti',
                    'Tila',
                    'Keskeytetty',
                    'Palautettu',
                    'Jatkettu',
                    'Vaihdettu',
                    'Järjestysnro',
                ],
                rowDelimiter: ';',
            },
            (err, csv) => {
                if (err) {
                    console.error('Export error:', err);
                    notify(translate('notifications.EXPORT_ERROR'), {
                        type: 'error',
                    });
                    return;
                }
                // UTF-8 BOM, jotta Excel näyttää ääkköset oikein.
                downloadCSV(`\ufeff${csv}`, 'lisenssit');
            }
        );
    };

    return (
        <List
            perPage={50}
            pagination={
                <Pagination
                    rowsPerPageOptions={[
                        10,
                        25,
                        50,
                        100,
                        200,
                        { label: 'Kaikki (max 500)', value: 500 },
                    ]}
                />
            }
            {...props}
            actions={
                enableExport ? (
                    <TopToolbar>
                        <ExportButton
                            label={translate('actions.export')}
                            maxResults={500}
                        />
                    </TopToolbar>
                ) : (
                    false
                )
            }
            exporter={enableExport ? exporter : undefined}
            filter={filter}
            filters={
                showExtra.includes('search')
                    ? [
                          <CustomSelectInput
                              url="extralist/subscription/groups"
                              source="group_guid"
                              label={translate(
                                  'resources.userlicense.fields.groups'
                              )}
                              filter="guid"
                              alwaysOn
                          />,
                          <TextInput
                              source="nrosrc"
                              label={translate(
                                  'resources.userlicense.fields.nrosrc'
                              )}
                              alwaysOn
                          />,
                          <TextInput
                              source="usersrc"
                              label={translate(
                                  'resources.userlicense.fields.usersrc'
                              )}
                              alwaysOn
                          />,
                      ]
                    : undefined
            }
            empty={<CustomEmpty />}
        >
            <Datagrid bulkActionButtons={bulkActionButtons}>
                {!hideFields.includes('lastname') && (
                    <TextField
                        source="lastname"
                        label={translate(
                            'resources.userlicense.fields.lastname'
                        )}
                        sortable={sortable}
                    />
                )}
                {!hideFields.includes('firstname') && (
                    <TextField
                        source="firstname"
                        label={translate(
                            'resources.userlicense.fields.firstname'
                        )}
                        sortable={sortable}
                    />
                )}
                {!hideFields.includes('email') && (
                    <TextField
                        source="email"
                        label={translate('resources.userlicense.fields.email')}
                        sortable={sortable}
                    />
                )}
                {!hideFields.includes('licenses') && (
                    <CustomUserLicenseField
                        source="licenses"
                        label={translate(
                            'resources.userlicense.fields.licenses'
                        )}
                    />
                )}
            </Datagrid>
        </List>
    );
};

export default CustomUserLicenseList;
